/* ──────────────────────────────────────────────────────────────────────
 *  FlipCard — shared 3D card-flip primitive (DEV-1440 reconciliation).
 *
 *  Extracted so flip surfaces SHARE the flip internals rather than copy
 *  them. Owns the flip shell + interaction parity:
 *    - hover  (pointer: fine)   → flip on enter, unflip on leave
 *    - tap    (pointer: coarse) → toggle
 *    - focus  (keyboard)        → flip on focus-in, unflip on focus-out
 *    - prefers-reduced-motion   → no animation (CSS)
 *
 *  Callers pass `front` / `back` content and size the card via `className`
 *  on the container. `FellowMemberCard` consumes this today; migrating
 *  `FlipTeamMemberCard` (9 surfaces) onto it is a follow-up.
 * ────────────────────────────────────────────────────────────────────── */

import React, { useState, useCallback, useRef } from 'react';
import styles from './FlipCard.module.css';

export interface FlipCardProps {
  front: React.ReactNode;
  back: React.ReactNode;
  /** When false, the card renders flat (front only) and does not flip. */
  canFlip?: boolean;
  /** Sizing/skin class applied to the perspective container. */
  className?: string;
  ariaLabel?: string;
}

function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

export default function FlipCard({
  front,
  back,
  canFlip = true,
  className,
  ariaLabel,
}: FlipCardProps) {
  const isTouch = useRef(isTouchDevice());
  const [flipped, setFlipped] = useState(false);

  const handleMouseEnter = useCallback(() => {
    if (!canFlip || isTouch.current) return;
    setFlipped(true);
  }, [canFlip]);
  const handleMouseLeave = useCallback(() => {
    if (!canFlip || isTouch.current) return;
    setFlipped(false);
  }, [canFlip]);
  const handleClick = useCallback(() => {
    if (!canFlip || !isTouch.current) return;
    setFlipped((p) => !p);
  }, [canFlip]);
  const handleFocus = useCallback(() => {
    if (!canFlip) return;
    setFlipped(true);
  }, [canFlip]);
  const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    // Unflip only when focus leaves the whole card (not when moving between
    // back-face controls).
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setFlipped(false);
    }
  }, []);

  if (!canFlip) {
    return (
      <div className={className}>
        <div className={styles.inner}>
          <div className={styles.front}>{front}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.container} ${className ?? ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      tabIndex={0}
      role="group"
      aria-label={ariaLabel}
    >
      <div className={`${styles.inner} ${flipped ? styles.flipped : ''}`}>
        <div className={styles.front}>{front}</div>
        <div className={styles.back}>{back}</div>
      </div>
    </div>
  );
}
